import { useEffect } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { paymentApi } from '../services/paymentApi';
import { usePaymentStore } from '../state/usePaymentStore';

/**
 * ─── usePaymentBootstrap ─────────────────────────────────────────────────────
 *
 * À appeler UNE fois au niveau racine de l'app (App.tsx).
 * Hydrate la config du module (interrupteur admin, plans, limites gratuites)
 * depuis le backend : la décision "paiements ON/OFF" devient globale à tous
 * les visiteurs, plus seulement locale au navigateur de l'admin.
 *
 * Échec réseau / backend absent (dev) → silencieux : l'état local persisté
 * (défaut : module désactivé) reste la source, l'app fonctionne normalement.
 */
export function usePaymentBootstrap(): void {
  const hydrateFromServer = usePaymentStore((s) => s.hydrateFromServer);
  const setSubscription = usePaymentStore((s) => s.setSubscription);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isGuestSession = useAuthStore((s) => s.isGuestSession);

  useEffect(() => {
    let cancelled = false;
    paymentApi
      .fetchPublicConfig()
      .then(({ data }) => {
        if (!cancelled) hydrateFromServer(data);
      })
      .catch(() => {
        /* backend indisponible → défauts locaux */
      });
    return () => {
      cancelled = true;
    };
  }, [hydrateFromServer]);

  // Abonnement rattaché au compte : re-hydraté à chaque (re)connexion.
  // C'est lui qui fait foi — les données locales sont purgées au logout.
  useEffect(() => {
    if (!isAuthenticated || isGuestSession) return;
    let cancelled = false;
    paymentApi
      .getMySubscription()
      .then(({ data }) => {
        if (!cancelled && data.subscription) setSubscription(data.subscription);
      })
      .catch(() => {
        /* offline → on garde l'état local */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isGuestSession, setSubscription]);
}
